/**
 * Shared Apple Health metric name constants and normalization.
 *
 * Centralizes the metric name lists and normalization logic used across
 * useDashboardData, Activity, History, PatientDetail, Phenotype, and ReportButton.
 *
 * Metric names come from the native HealthKit mobile app in Title Case
 * (e.g. 'Steps', 'HRV', 'Active Energy'). Legacy snake_case variants
 * (e.g. 'step_count') are kept for backward compatibility with existing data.
 */

/** All known Apple Health metric names to include in Supabase `.in()` filters */
export const APPLE_HEALTH_METRIC_NAMES = [
  // Steps
  'Steps', 'step_count', 'step count', 'Step Count',
  // Active Energy
  'Active Energy', 'Active Energy Burned', 'active_energy', 'active_energy_burned', 'activeenergy',
  // HRV
  'HRV', 'hrv', 'heart_rate_variability', 'Heart Rate Variability',
  // Resting Heart Rate
  'Resting Heart Rate', 'resting_heart_rate',
  // SpO2
  'Blood Oxygen Saturation', 'blood_oxygen_saturation', 'SpO2',
  // Distance
  'Walking + Running Distance', 'walking_running_distance',
  // Weight
  'Body Mass', 'body_mass', 'Weight', 'weight', 'weight_body_mass',
  // Exercise
  'Exercise Time', 'Exercise Minutes', 'exercise_time', 'apple_exercise_time',
  // Daylight
  'Time in Daylight', 'Time In Daylight', 'time_in_daylight',
  // Mindful Minutes
  'Mindful Minutes', 'mindful_minutes',
  // Respiratory Rate
  'Respiratory Rate', 'respiratory_rate',
  // BMI
  'BMI', 'bmi', 'Body Mass Index', 'body_mass_index',
  // Body Fat
  'Body Fat Percentage', 'body_fat_percentage', 'Body Fat', 'body_fat',
  // VO2 Max
  'VO2 Max', 'vo2_max', 'Vo2Max', 'vo2max',
] as const;

/**
 * Normalize a metric name to a consistent lowercase/underscore form for routing.
 * e.g. 'Walking + Running Distance' → 'walking_+_running_distance'
 */
export function normalizeMetricName(name: string): string {
  return name.toLowerCase().replace(/[\s+]+/g, '_').replace(/_+/g, '_');
}

/**
 * Classify a normalized metric name into a category for routing in data processing.
 */
export type MetricCategory =
  | 'steps' | 'exercise' | 'active_energy' | 'hrv'
  | 'resting_hr' | 'spo2' | 'distance' | 'weight'
  | 'bmi' | 'body_fat' | 'vo2max'
  | 'daylight' | 'mindful' | 'respiratory_rate' | 'unknown';

export function classifyMetric(rawName: string): MetricCategory {
  const name = normalizeMetricName(rawName);
  if (name.includes('step')) return 'steps';
  if (name.includes('exercise')) return 'exercise';
  if (name.includes('active_energy') || name.includes('activeenergy')) return 'active_energy';
  if (name === 'hrv' || name.includes('heart_rate_variability')) return 'hrv';
  if (name.includes('resting') && name.includes('heart')) return 'resting_hr';
  if (name.includes('oxygen') || name.includes('spo2')) return 'spo2';
  if (name.includes('distance') || name.includes('walking')) return 'distance';
  if (name === 'bmi' || name.includes('body_mass_index')) return 'bmi';
  if (name.includes('body_fat')) return 'body_fat';
  if (name.includes('vo2')) return 'vo2max';
  if (name.includes('body_mass') || name.includes('weight')) return 'weight';
  if (name.includes('daylight')) return 'daylight';
  if (name.includes('mindful')) return 'mindful';
  if (name.includes('respiratory')) return 'respiratory_rate';
  return 'unknown';
}
